import {
  BagelDocument,
  BagelDocumentCategory,
  BagelDocumentDomain,
  BagelDocumentFileType,
  BagelDocumentIngestSource,
  BagelDocumentStatus,
} from '../../../types';

export type BagelDocumentTableRow = BagelDocument & {
  ingestSource: BagelDocumentIngestSource;
  ingestSourceLabel: string;
  ingestYear: string;
  sourceFileLabel: string;
  docTypeLabel: string;
};

const INGEST_SOURCE_LABEL: Record<BagelDocumentIngestSource, string> = {
  rosie: 'Rosie upload',
  mcp: 'MCP ingest',
};

export function resolveIngestSource(
  doc: BagelDocument,
): BagelDocumentIngestSource {
  if (doc.metadata?.ingestSource) return doc.metadata.ingestSource;
  return doc.domain === 'scla' ? 'mcp' : 'rosie';
}

export function resolveSourceFileLabel(doc: BagelDocument): string {
  return (
    doc.originalFileName?.trim() ||
    doc.metadata?.sourceFile?.trim() ||
    doc.name.trim()
  );
}

export function normalizeBagelDocumentRow(
  doc: BagelDocument,
): BagelDocumentTableRow {
  const ingestSource = resolveIngestSource(doc);
  return {
    ...doc,
    ingestSource,
    ingestSourceLabel: INGEST_SOURCE_LABEL[ingestSource],
    ingestYear: doc.metadata?.year?.trim() || '',
    sourceFileLabel: resolveSourceFileLabel(doc),
    docTypeLabel: doc.metadata?.docType?.trim() || '',
  };
}

export function documentMatchesSearch(
  doc: BagelDocumentTableRow,
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const haystack = [
    doc.name,
    doc.description,
    doc.originalFileName,
    doc.sourceFileLabel,
    doc.docTypeLabel,
    doc.category,
    doc.fileType,
    doc.domain,
    doc.ingestSource,
    doc.ingestYear,
    doc.metadata?.sourceFile,
    doc.metadata?.docType,
    doc.metadata?.title,
    doc.errorMessage,
    ...(doc.metadata?.tags ?? []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return haystack.includes(q);
}

export type BagelDocumentFilterState = {
  ingestSource: BagelDocumentIngestSource | 'all';
  domain: BagelDocumentDomain | 'all';
  category: BagelDocumentCategory | 'all' | 'null';
  status: BagelDocumentStatus | 'all';
  year: string;
  fileType: BagelDocumentFileType | 'all';
};

export const FILE_TYPE_FILTER_OPTIONS: Array<{
  label: string;
  value: BagelDocumentFileType;
}> = [
  { label: 'PDF', value: 'pdf' },
  { label: 'DOCX', value: 'docx' },
  { label: 'CSV', value: 'csv' },
  { label: 'TXT', value: 'txt' },
  { label: 'MD', value: 'md' },
  { label: 'XLSX', value: 'xlsx' },
];

export const DEFAULT_DOCUMENT_FILTERS: BagelDocumentFilterState = {
  ingestSource: 'all',
  domain: 'all',
  category: 'all',
  status: 'all',
  year: 'all',
  fileType: 'all',
};

export function filterBagelDocuments(
  docs: BagelDocumentTableRow[],
  search: string,
  filters: BagelDocumentFilterState,
): BagelDocumentTableRow[] {
  return docs.filter(doc => {
    if (!documentMatchesSearch(doc, search)) return false;
    if (
      filters.ingestSource !== 'all' &&
      doc.ingestSource !== filters.ingestSource
    ) {
      return false;
    }
    if (filters.domain !== 'all' && doc.domain !== filters.domain) {
      return false;
    }
    if (filters.category === 'null') {
      if (doc.category != null) return false;
    } else if (
      filters.category !== 'all' &&
      doc.category !== filters.category
    ) {
      return false;
    }
    if (filters.status !== 'all' && doc.status !== filters.status) {
      return false;
    }
    if (filters.year !== 'all' && doc.ingestYear !== filters.year) {
      return false;
    }
    if (filters.fileType !== 'all' && doc.fileType !== filters.fileType) {
      return false;
    }
    return true;
  });
}

export function collectIngestYears(
  docs: BagelDocumentTableRow[],
): string[] {
  const years = new Set<string>();
  docs.forEach(doc => {
    if (doc.ingestYear) years.add(doc.ingestYear);
  });
  return Array.from(years).sort((a, b) => b.localeCompare(a));
}
